import { App } from "vue";
import { AxiosInstance } from "axios";
import { createApi, SLKS_API_KEY, SLKS_HTTP_KEY, SlksApi } from "./api";
import TrapFocusDirective from "./util/trap-focus";
import ClickedOutside from "./util/ClickedOutside.vue";
import { Store } from "vuex";
import { createNotifications, NotificationsPlugin, NOTIFICATIONS_KEY } from "./notifications";

export interface CoreOptions {
    store: Store<unknown>;
}

export default function createCore() {
    const api = createApi();
    const notifications = createNotifications();

    return {
        api,
        notifications,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        install(app: App, { store }: CoreOptions) {
            app.provide(NOTIFICATIONS_KEY, notifications);
            app.config.globalProperties.$notifications = notifications;

            app.provide(SLKS_API_KEY, api);
            app.config.globalProperties.$api = api;

            app.provide(SLKS_HTTP_KEY, api.http);
            app.config.globalProperties.$http = api.http;

            app.component("ClickedOutside", ClickedOutside);
            app.directive("trap-focus", TrapFocusDirective);
        },
    };
}

export type { SlksApi };
export { useHttp, useAPI } from "./api";
export { useApiResource, useSingleResource, useDeleteResource } from "./api-resource";

// export entities methods
export { useArtistsResource, useArtists } from "./entities/artist";
export { useArtworksResource, useArtworks } from "./entities/artwork";
export { useTaxonomiesResource, useTaxonomies } from "./entities/translatable-entity";
export { useTagsResource, useTags } from "./entities/tag";
export { useImages } from "./entities/image";
export * from "./search/useSearch"; // here we want to export everything including types
export { useFilterOptions } from "./search/useFilterOptions";
export { useMemoryCachedSearch, purgeSearchMemoryCache } from "./search/useMemoryCachedSearch";

// export useful utilities
export { useLocalStorage } from "./util/local-storage";
export { useColumnView } from "./util/column-view";
export { useDocumentTitle } from "./util/document-title";
export { useScrollTransition } from "./util/scroll-transition";
export { useViewport, BREAKPOINTS } from "./util/viewport";
export { useFormattedDate, useFormattedEsDate } from "./util/formatted-date";
export { useGettersAndSetters } from "./util/getters-and-setters";
export { useMobileKeyboard } from "./util/mobile-keyboard";
export { useTokenCredentials } from "./util/token-credentials";
export type { SlksDateFormat } from "./util/formatted-date";
export type { BreakpointsMap, BreakpointName, UseViewport } from "./util/viewport";
export { Deferred } from "./util/deferred";
export { identity } from "./util/identity";

// Export useful types
export * from "./util/types";
export * from "./dto/token";
export * from "./dto/hydra";
export * from "./dto/date";
export * from "./dto/translatable-entity";
export * from "./dto/artwork";
export * from "./dto/artist";
export * from "./dto/museum";
export * from "./dto/image";
export * from "./dto/tag";
export * from "./search/filters";

// Notifications

export { useNotifications, createNotifications, createNotificationsMock } from "./notifications";
export type { NotificationsPlugin, NotificationsPluginMock, Notification, NotificationType } from "./notifications";

declare module "@vue/runtime-core" {
    export interface ComponentCustomProperties {
        $api: SlksApi;
        $http: AxiosInstance;
        $notifications: NotificationsPlugin;
    }
}
